import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';

const PIN_LENGTH = 6;

function PinDots({ value }) {
  return (
    <div className="flex items-center justify-center gap-2.5 my-6">
      {Array.from({ length: PIN_LENGTH }).map((_, i) => (
        <div
          key={i}
          className={`w-3 h-3 rounded-full border-2 transition-colors ${
            value.length > i ? 'bg-accent border-accent' : 'border-border'
          }`}
        />
      ))}
    </div>
  );
}

export default function PinPad({ employee, mode, onSuccess, onBack }) {
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [stage, setStage] = useState(mode === 'setup' ? 'setup' : 'verify');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  // PIN-reset (magic link) state. Lives alongside the PIN flow so a user
  // who can't remember their PIN doesn't have to log out and back in.
  const [resetOpen, setResetOpen] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetError, setResetError] = useState(null);

  const active = stage === 'confirm' ? confirmPin : pin;
  const setActive = v => (stage === 'confirm' ? setConfirmPin(v) : setPin(v));

  useEffect(() => {
    inputRef.current?.focus();
  }, [stage]);

  useEffect(() => {
    if (active.length !== PIN_LENGTH) return;

    if (stage === 'verify') {
      setBusy(true);
      api.verifyPin(employee.id, active)
        .then(() => onSuccess())
        .catch(e => {
          setError(e.message);
          setPin('');
        })
        .finally(() => setBusy(false));
    } else if (stage === 'setup') {
      setError(null);
      setStage('confirm');
    } else if (stage === 'confirm') {
      if (confirmPin !== pin) {
        setError("PINs don't match. Try again.");
        setPin('');
        setConfirmPin('');
        setStage('setup');
        return;
      }
      setBusy(true);
      api.setPin(employee.id, pin)
        .then(() => onSuccess())
        .catch(e => {
          setError(e.message);
          setPin('');
          setConfirmPin('');
          setStage('setup');
        })
        .finally(() => setBusy(false));
    }
  }, [active]);

  const heading = stage === 'verify'
    ? 'Enter your PIN'
    : stage === 'setup'
      ? `Create a ${PIN_LENGTH}-digit PIN`
      : 'Confirm your PIN';

  const subline = stage === 'verify'
    ? `Welcome back, ${employee.name.split(' ')[0]}`
    : stage === 'setup'
      ? "You'll use this PIN every time you log in."
      : `Enter the same ${PIN_LENGTH} digits again.`;

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <button
          onClick={onBack}
          className="text-muted hover:text-text text-sm flex items-center gap-1 mb-6"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M15 18l-6-6 6-6" />
          </svg>
          Not you? Switch
        </button>

        <div className="text-center">
          <h1 className="text-xl font-semibold">{heading}</h1>
          <p className="text-muted text-sm mt-1">{subline}</p>
        </div>

        <PinDots value={active} />

        <input
          ref={inputRef}
          type="tel"
          inputMode="numeric"
          maxLength={PIN_LENGTH}
          pattern={`\\d{${PIN_LENGTH}}`}
          value={active}
          disabled={busy}
          onChange={e => {
            const v = e.target.value.replace(/\D/g, '').slice(0, PIN_LENGTH);
            setActive(v);
            setError(null);
          }}
          className="w-full text-center text-2xl tracking-[0.4em] font-mono py-3 px-4 rounded-lg bg-panel border border-border focus:border-accent focus:outline-none"
          placeholder={'•'.repeat(PIN_LENGTH)}
        />

        {error && (
          <div className="mt-4 text-center text-sm text-danger">{error}</div>
        )}

        {busy && (
          <div className="mt-4 text-center text-sm text-muted">Verifying...</div>
        )}

        {/* Forgot-PIN affordance is only useful on the verify stage —
            during setup/confirm the user is choosing a new PIN. */}
        {stage === 'verify' && !resetOpen && (
          <div className="mt-6 text-center">
            <button
              onClick={() => { setResetOpen(true); setResetError(null); }}
              className="text-sm text-muted hover:text-accent transition-colors"
            >
              Forgot PIN?
            </button>
          </div>
        )}

        {stage === 'verify' && resetOpen && !resetSent && (
          <div className="mt-6 p-4 rounded-lg border border-border bg-panel2/50">
            <p className="text-sm">
              We'll email a reset link to{' '}
              <span className="font-medium">{employee.email}</span>. The link
              expires in 10 minutes.
            </p>
            {resetError && (
              <div className="mt-3 text-sm text-danger">{resetError}</div>
            )}
            <div className="mt-4 flex items-center gap-2">
              <button
                disabled={resetBusy}
                onClick={() => {
                  setResetBusy(true);
                  setResetError(null);
                  api.requestPinReset(employee.email)
                    .then(() => setResetSent(true))
                    .catch(e => setResetError(e.message))
                    .finally(() => setResetBusy(false));
                }}
                className="px-3 py-1.5 rounded-md bg-accent hover:bg-accent2 text-white text-sm font-semibold transition-colors"
              >
                {resetBusy ? 'Sending…' : 'Send reset link'}
              </button>
              <button
                disabled={resetBusy}
                onClick={() => setResetOpen(false)}
                className="px-3 py-1.5 rounded-md text-sm text-muted hover:text-text transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {stage === 'verify' && resetSent && (
          <div className="mt-6 p-4 rounded-lg border border-running/30 bg-running/10">
            <p className="text-sm">
              Check your inbox at{' '}
              <span className="font-medium">{employee.email}</span> for the
              reset link. Click it to set a new PIN. If you don't see it within
              a minute, check spam.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
